<!--
  BaseFilterDialog.vue — Filter dialog wrapper extending BaseDialog with filter defaults.

  Filter changes auto-apply in real-time via a parent watcher.
  - Clear button: emits @clear (parent resets filters), then closes
  - X button / click-outside: just closes, does NOT reset filters

  Props: Same as BaseDialog with overridden defaults.
  Events:
    clear — emitted when Clear button is clicked (reset filters to defaults)
-->
<template>
  <BaseDialog
    :model-value="modelValue"
    @update:model-value="handleModelUpdate"
    :title="title"
    :subtitle="subtitle"
    :title-icon="titleIcon"
    :title-icon-color="titleIconColor"
    :max-width="maxWidth"
    :persistent="persistent"
    :scrollable="scrollable"
    :save-text="saveText"
    :save-color="saveColor"
    :save-loading="saveLoading"
    :save-disabled="saveDisabled"
    :show-save="showSave"
    :cancel-text="cancelText"
    cancel-action="emit"
    :toolbar-color="toolbarColor"
    @cancel="onClear"
    @save="onDone"
  >
    <slot />
    <template #actions><slot name="actions" /></template>
    <template #toolbar-extra><slot name="toolbar-extra" /></template>
  </BaseDialog>
</template>

<script setup>
import BaseDialog from "@/components/BaseDialog.vue";

defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: "Filters" },
  subtitle: { type: String, default: null },
  maxWidth: { type: [Number, String], default: 500 },
  persistent: { type: Boolean, default: false },
  scrollable: { type: Boolean, default: true },
  saveText: { type: String, default: "Apply" },
  saveColor: { type: String, default: "primary" },
  saveLoading: { type: Boolean, default: false },
  saveDisabled: { type: Boolean, default: false },
  showSave: { type: Boolean, default: true },
  cancelText: { type: String, default: "Clear" },
  toolbarColor: { type: String, default: "primary" },
  // The funnel on every filter window, like the filter button that opens it (2026-10-30: one filter icon app-wide)
  titleIcon: { type: String, default: "mdi-filter-variant" },
  titleIconColor: { type: String, default: undefined },
});

const emit = defineEmits(["update:modelValue", "save", "clear"]);

// Flag to distinguish action-button closes from X/click-outside closes
let fromAction = false;

/** Clear button: emit clear event then close dialog */
function onClear() {
  fromAction = true;
  emit("clear");
  emit("update:modelValue", false);
}

/** Apply button: emit save event then close dialog */
function onDone() {
  fromAction = true;
  emit("save");
  emit("update:modelValue", false);
}

/**
 * Intercept model-value changes from BaseDialog.
 * X button / click-outside: just close, do not reset filters.
 * Filter changes auto-apply; Clear button explicitly resets.
 */
function handleModelUpdate(val) {
  if (val) {
    fromAction = false;
    emit("update:modelValue", val);
    return;
  }
  // Dialog closing — just close (do not emit clear)
  emit("update:modelValue", val);
  fromAction = false;
}
</script>
